/* ============================================================
   SINGSPACE ADMIN - MONITORING MESSAGES WHATSAPP (Sobre)
   ============================================================ */

import { Bot as BotIcon, MessageSquare, Search, User } from 'lucide-react';
import { Message, SessionStatus } from '../../types/whatsapp.types';
import { Card } from '../ui/card';
import { Input } from '../ui/input';

interface MessagesViewProps {
  messages: Message[];
  sessionStatus: SessionStatus;
  msgEndRef: React.RefObject<HTMLDivElement>;
}

export function MessagesView({ messages, sessionStatus, msgEndRef }: MessagesViewProps) {
  return (
    <Card className="border border-slate-200 flex flex-col h-[600px]">
      {/* Header */}
      <div className="p-4 border-b border-slate-100 flex items-center justify-between bg-white">
        <div className="flex items-center gap-3">
          <MessageSquare size={20} className="text-slate-600" />
          <div>
            <h3 className="text-sm font-semibold text-slate-900">Flux de Conversation</h3>
            <p className="text-xs text-slate-500">Monitoring en temps réel</p>
          </div>
        </div>

        <div className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-slate-50 border border-slate-100">
          <Search size={14} className="text-slate-400" />
          <Input
            placeholder="Rechercher..."
            className="h-6 text-xs border-0 bg-transparent focus-visible:ring-0 p-0 w-32"
          />
        </div>
      </div>

      {/* Messages List */}
      <div className="flex-1 overflow-y-auto p-4 space-y-3 bg-slate-50">
        {messages.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-full text-slate-400">
            <MessageSquare size={48} className="mb-3 opacity-20" />
            <p className="text-sm">Aucun message pour le moment</p>
            <p className="text-xs mt-1">Les conversations apparaîtront ici</p>
          </div>
        ) : (
          messages.map((msg) => (
            <div
              key={msg.id}
              className="bg-white border border-slate-200 rounded-lg p-3 hover:border-slate-300 transition-colors"
            >
              <div className="flex items-start justify-between mb-2">
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 rounded-full bg-slate-100 flex items-center justify-center">
                    <User size={16} className="text-slate-600" />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-slate-900">{msg.fromName}</p>
                    <p className="text-xs text-slate-500">{msg.from}</p>
                  </div>
                </div>
                <span className="text-xs text-slate-400">
                  {msg.timestamp.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}
                </span>
              </div>

              <p className="text-sm text-slate-700 mb-2">{msg.body}</p>

              {msg.botReplied && (
                <div className="flex items-center gap-2 px-2 py-1 bg-emerald-50 rounded border border-emerald-100">
                  <BotIcon size={12} className="text-emerald-600" />
                  <span className="text-xs text-emerald-700 font-medium">Réponse automatique envoyée</span>
                </div>
              )}

              {!msg.botReplied && sessionStatus === 'connected' && (
                <div className="flex items-center gap-2 px-2 py-1 bg-amber-50 rounded border border-amber-100">
                  <div className="w-1.5 h-1.5 rounded-full bg-amber-500" />
                  <span className="text-xs text-amber-700 font-medium">En attente de traitement</span>
                </div>
              )}
            </div>
          ))
        )}
        <div ref={msgEndRef} />
      </div>

      {/* Footer Stats */}
      <div className="p-3 border-t border-slate-100 bg-white flex items-center justify-between text-xs">
        <span className="text-slate-500">
          Total: <span className="font-medium text-slate-700">{messages.length}</span> messages
        </span>
        <span className="text-slate-500">
          Traités: <span className="font-medium text-emerald-600">{messages.filter((m) => m.botReplied).length}</span>
        </span>
        <span className="text-slate-500">
          En attente: <span className="font-medium text-amber-600">{messages.filter((m) => !m.botReplied).length}</span>
        </span>
      </div>
    </Card>
  );
}
